import React, { Component } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { apiService } from '../../services/api';
import { getOAuthCallbackCode, getOAuthError } from '../../utils/oauth';
import styles from './Auth.module.scss';

interface OAuthCallbackState {
  loading: boolean;
  error: string | null;
}

class OAuthCallback extends Component<any, OAuthCallbackState> {
  constructor(props: any) {
    super(props);
    this.state = {
      loading: true,
      error: null
    };
  }

  async componentDidMount() {
    try {
      // Get authorization code from URL
      const code = getOAuthCallbackCode();
      const error = getOAuthError();

      if (error) {
        console.error('[OAuthCallback] OAuth error:', error);
        this.setState({
          loading: false,
          error: `OAuth error: ${error}`
        });
        // Redirect to login after 3 seconds
        setTimeout(() => {
          this.props.navigate('/login');
        }, 3000);
        return;
      }

      if (!code) {
        console.error('[OAuthCallback] No authorization code found');
        this.setState({
          loading: false,
          error: 'No authorization code received'
        });
        // Redirect to login after 3 seconds
        setTimeout(() => {
          this.props.navigate('/login');
        }, 3000);
        return;
      }

      console.log('[OAuthCallback] Received authorization code, exchanging for tokens...');

      // Exchange authorization code for tokens via backend
      // The backend should exchange the code with Google and return your app's tokens
      const redirectUri = window.location.origin + '/oauth/callback';
      const response = await apiService.exchangeOAuthCode(code, redirectUri);

      if (response.success && response.data) {
        if (response.data.accessToken) {
          apiService.setTokens(
            response.data.accessToken,
            response.data.refreshToken
          );

          // Verify profile after successful OAuth
          let isProfileComplete = false;
          try {
            const verifyResponse = await apiService.verifyCustomerProfile();
            if (verifyResponse.success && verifyResponse.data) {
              isProfileComplete = verifyResponse.data.isProfileComplete ?? false;
              console.log('Profile verified successfully after OAuth');
            }
          } catch (verifyError: any) {
            console.error('Profile verification failed:', verifyError);
          }

          // Check for stored redirect path
          const redirectPath = sessionStorage.getItem('redirectAfterLogin');
          if (redirectPath && isProfileComplete) {
            sessionStorage.removeItem('redirectAfterLogin');
            console.log('[OAuthCallback] Redirecting to stored path after OAuth:', redirectPath);
            this.props.navigate(redirectPath);
            return;
          }
          
          // Redirect based on profile completion
          if (isProfileComplete) {
            this.props.navigate('/customer/friends');
          } else {
            this.props.navigate('/profile');
          }
        } else {
          throw new Error('No access token found in response');
        }
      } else {
        throw new Error(response.message || 'OAuth token exchange failed');
      }
    } catch (error: any) {
      console.error('[OAuthCallback] Failed to complete OAuth:', error);
      this.setState({
        loading: false,
        error: error.message || 'Failed to complete sign-in'
      });
      // Redirect to login after 5 seconds
      setTimeout(() => {
        this.props.navigate('/login');
      }, 5000);
    }
  }

  render() {
    const { loading, error } = this.state;

    return (
      <div className={styles.authContainer}>
        <div className={styles.authCard}>
          {loading ? (
            <>
              <h2>Completing sign-in...</h2>
              <p>Please wait while we complete your Google sign-in.</p>
            </>
          ) : error ? (
            <>
              <h2>Sign-in Failed</h2>
              <p className={styles.errorMessage}>{error}</p>
              <p>Redirecting to login page...</p>
            </>
          ) : null}
        </div>
      </div>
    );
  }
}

// Wrapper component to use hooks
const OAuthCallbackWrapper = () => {
  const navigate = useNavigate();
  return <OAuthCallback navigate={navigate} />;
};

export default OAuthCallbackWrapper;

